import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { isEmpty } from 'lodash'

import { useWebCall } from './useWebCall'

const WebCallContext = createContext(null)

// A web call outlives the thing that starts it. The trigger is in the agent
// header and the call runs in a full-screen view of its own, so the hook that
// owns the mic and the socket lives above both, here.
//
// This also owns the session's call history. Deliberately *only* the session's:
// nothing is persisted, and leaving the call view drops it. The post-call
// pipeline is the durable record — this is the gap before it catches up, and
// keeping a private copy of transcripts past that gap would be a second,
// unmanaged home for call content.
export const WebCallProvider = ({ children }) => {
  const webCall = useWebCall()
  const [isScreenOpen, setScreenOpen] = useState(false)
  const [history, setHistory] = useState([])

  const { start, stop, reset, status, turns, callId } = webCall

  // Identifies the call currently on screen for archiving. Not `callId`: a call
  // that fails before the agent acks never gets one, and two such calls would
  // then look like the same call and only be archived once.
  const currentCallRef = useRef(null)

  const openScreen = useCallback(() => {
    setScreenOpen(true)
  }, [])

  // Opening the view and placing the call are separate on purpose. The view is
  // where you decide to call — landing in it should not already be dialling,
  // any more than opening a phone app should.
  const startCall = useCallback(
    (agentId, callerNumber = null) => {
      currentCallRef.current = { key: `call-${Date.now()}`, startedAt: Date.now() }
      start(agentId, { callerNumber })
    },
    [start]
  )

  const hangUp = useCallback(() => {
    stop()
  }, [stop])

  // Archive the call once it is over, so the operator can still read what was
  // said and copy the id while they decide whether to call again.
  //
  // A connected call with no turns still gets archived: nobody spoke, but the
  // call happened and its Cartesia id is the whole reason this list exists.
  // What is skipped is a call that never connected at all — a blocked
  // microphone leaves no id and nothing said, so a row for it would carry no
  // information.
  //
  // `current.archived` is set synchronously below, before the state update, so
  // a trailing turn or a late `callId` re-running this effect cannot slip a
  // second row through.
  useEffect(() => {
    const { current } = currentCallRef
    const isOver = status === 'ended' || status === 'error'
    const happened = Boolean(callId) || !isEmpty(turns)
    if (!isOver || !current || current.archived || !happened) {
      return
    }
    current.archived = true
    setHistory((existing) => [
      { key: current.key, callId, turns, startedAt: current.startedAt },
      ...existing,
    ])
  }, [status, turns, callId])

  // Leaving is what clears the history — the one place the promise in the
  // view's notice is actually kept.
  const closeScreen = useCallback(() => {
    reset()
    setHistory([])
    currentCallRef.current = null
    setScreenOpen(false)
  }, [reset])

  const value = useMemo(
    () => ({ ...webCall, isScreenOpen, history, openScreen, startCall, hangUp, closeScreen }),
    [webCall, isScreenOpen, history, openScreen, startCall, hangUp, closeScreen]
  )

  return <WebCallContext.Provider value={value}>{children}</WebCallContext.Provider>
}

export const useWebCallContext = () => useContext(WebCallContext)
